import styles from "./EventDetail.module.scss";
import { GlobeIcon } from "../components/Icons/Globe";
import { GalleryIcon } from "../components/Icons/Gallery";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { EventContext } from "../context/EventContext";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import {
  Avatar,
  Button,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { format } from "date-fns";
import { useTranslation } from "../i18n";
import { dateLocale } from "../i18n/dateLocale";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { LightningIcon } from "../components/Icons/Lightning";
import { WarningIcon } from "../components/Icons/Warning";
import classNames from "classnames";
import { InviteFriendsDialog } from "../components/InviteFriendsDialog/InviteFriendsDialog";
import { CompleteProfileDialog } from "../components/CompleteProfileDialog/CompleteProfileDialog";
import { useAlert } from "../context/AlertContext";
import { fetcher, isFetcherError } from "../utils/fetcher";
import { AuthContext } from "../context/AuthContext";
import { getInitials } from "../utils/getInitials";
import { Event, OrganisationData, PublicOrganisationData } from "../types";
import { useEnumLabels } from "../hooks/useEnumLabels";
import { validateAge } from "../utils/validation";

interface EventSessionResponse {
  previous_event_id: string | null;
  current_event_id: string;
  next_event_id: string | null;
}

export const EventDetail = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const eventContext = useContext(EventContext);
  const authContext = useContext(AuthContext);
  const { getSkillLabelById, getInterestLabelById, getEventTypeLabelById } =
    useEnumLabels();
  const { setAlert } = useAlert();
  const { t } = useTranslation();

  const events = eventContext?.events;
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const fetchVolunteersEvents =
    eventContext?.fetchVolunteersEvents ?? (async () => {});
  const currentUser = authContext?.currentUser;

  const [event, setEvent] = useState<Event | null>(null);
  const [eventOrganiser, setEventOrganiser] = useState<
    OrganisationData | PublicOrganisationData | null
  >(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [completeProfileDialogOpen, setCompleteProfileDialogOpen] =
    useState(false);

  useEffect(() => {
    const fetchEventAndOrganiser = async () => {
      setError(null);
      setLoading(true);

      try {
        const eventResponse = await fetcher<Event>({
          url: `/event/${eventId}`,
        });
        setEvent(eventResponse);

        const organiserResponse = await fetcher<
          OrganisationData | PublicOrganisationData
        >({
          url: `/organisation/${eventResponse.organiser?.organiser_id}`,
        });
        setEventOrganiser(organiserResponse);
      } catch (error) {
        setError("eventDetail.alerts.loadFailed");
        console.error("Error fetching event detail or organiser data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEventAndOrganiser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    const root = document.getElementById("root");
    if (root) {
      if (dialogOpen) {
        root.classList.add("dialog-blur");
      } else {
        root.classList.remove("dialog-blur");
      }
    }
    return () => {
      if (root) {
        root.classList.remove("dialog-blur");
      }
    };
  }, [dialogOpen]);

  const parseErrorDetail = (body?: string): string | undefined => {
    if (!body) return undefined;
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed.detail === "string") {
        return parsed.detail;
      }
    } catch {
      return undefined;
    }
    return undefined;
  };

  const handleNext = async () => {
    if (!eventId) {
      setError("eventDetail.notFoundDetail");
      return;
    }

    try {
      const session = await fetcher<EventSessionResponse>({
        url: "/events/event-session/step",
        method: "POST",
        body: JSON.stringify({
          direction: "next",
          current_event_id: eventId,
        }),
      });
      navigate(`/event/${session.current_event_id}`);
      return;
    } catch (error) {
      const canReinit =
        isFetcherError(error) &&
        (error.status === 404 ||
          (error.status === 400 &&
            parseErrorDetail(error.body) === "Invalid current_event_id"));

      if (canReinit) {
        try {
          const sessionInit = await fetcher<EventSessionResponse>({
            url: "/events/event-session/init",
            method: "POST",
          });
          navigate(`/event/${sessionInit.current_event_id}`);
          return;
        } catch (initError) {
          console.error("Error reinitializing event session:", initError);
        }
      }

      console.error("Error stepping through recommended events:", error);
      setError("eventDetail.alerts.recommendedFailed");
    }
  };

  const handleAttendClick = () => {
    const dateOfBirth = (currentUser?.userData as any)?.date_of_birth;
    const hasDateOfBirth =
      typeof dateOfBirth === "string" && dateOfBirth.trim().length > 0;

    if (!hasDateOfBirth) {
      setCompleteProfileDialogOpen(true);
      return;
    }

    if (!validateAge(dateOfBirth)) {
      setAlert(
        true,
        "warning",
        t("eventDetail.alerts.minAge"),
      );
      return;
    }

    setDialogOpen("confirm");
  };

  const handleAttendConfirm = async () => {
    if (!event) return;

    const body = {
      event_id: event.id,
    };
    try {
      await fetcher<void>({
        url: "/match",
        method: "POST",
        body: JSON.stringify(body),
      });

      // Refetch volunteer's events to get updated data
      await fetchVolunteersEvents();

      // Refetch event data to update attendee count and show updated UI
      const updatedEvent = await fetcher<Event>({ url: `/event/${eventId}` });
      setEvent(updatedEvent);

      setAlert(true, "success", t("eventDetail.alerts.joined"));
      setDialogOpen("");

      // Navigate to favorites after a short delay to show the updated UI
      setTimeout(() => {
        navigate("/volunteer/favorite");
      }, 1500);
    } catch (error) {
      console.error("Error attending an event:", error);
      setAlert(
        true,
        "warning",
        t("eventDetail.alerts.joinFailed"),
      );
      setDialogOpen("");
    }
  };

  const handleAttendCancel = async (id?: string) => {
    if (!id) {
      setAlert(
        true,
        "error",
        t("eventDetail.alerts.cancelNoUser"),
      );
      setDialogOpen("");
    } else {
      try {
        await fetcher<void>({
          url: `/match/${id}`,
          method: "PUT",
          body: JSON.stringify({ status: "cancelled_vol" }),
        });

        // Refetch event data to update attendee count
        const updatedEvent = await fetcher<Event>({ url: `/event/${eventId}` });
        setEvent(updatedEvent);

        // Refetch volunteer's events to get updated data
        await fetchVolunteersEvents();

        setAlert(
          true,
          "info",
          t("eventDetail.alerts.cancelled"),
        );
        setDialogOpen("");
        navigate("/volunteer/events");
      } catch (error) {
        console.error("Error canceling attendance on an event:", error);
        setAlert(
          true,
          "error",
          t("eventDetail.alerts.cancelFailed"),
        );
        setDialogOpen("");
      }
    }
  };

  const handleDownloadCalendarEvent = async () => {
    try {
      const res = await fetcher<string>({
        url: `/event/export-calendar?event_id=${eventId}`,
        method: "POST",
      });

      const blob = new Blob([res], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "udalost.ics";
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);

      setAlert(true, "success", t("eventDetail.alerts.fileDownloaded"));
    } catch (error) {
      console.error("Error downloading calendar invite for an event:", error);
      setAlert(
        true,
        "warning",
        t("eventDetail.alerts.calendarFailed"),
      );
    }
  };

  if (loading)
    return (
      <div className={classNames(styles.eventDetail, styles.error)}>
        <CircularProgress />
      </div>
    );

  if (error)
    return (
      <div className={classNames(styles.eventDetail, styles.error)}>
        {t(error)}
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={() => navigate(`/event/${(events?.data as any)?.[0]?.id}`)}
        >
          {t("eventDetail.reviewRecommended")}
        </Button>
      </div>
    );

  if (!event || !eventOrganiser) {
    return (
      <div className={classNames(styles.eventDetail, styles.error)}>
        {t("eventDetail.unavailable")}
      </div>
    );
  }

  const isUserAttending = (event as any).my_match?.status === "approved";
  const matchId = (event as any).my_match?.id;
  const attendeesCount =
    typeof (event as any).match_count === "number"
      ? (event as any).match_count
      : 0;
  const previewMatches =
    Array.isArray((event as any).match_previews) &&
    (event as any).match_previews.length > 0
      ? (event as any).match_previews
      : [];
  const isEventFull =
    typeof (event as any).remaining_capacity === "number"
      ? (event as any).remaining_capacity <= 0
      : event.capacity
        ? attendeesCount >= event.capacity
        : false;

  const hasImage = event?.event_image?.[0];
  const hasAdditionalImages =
    event?.event_image &&
    event.event_image.length > 0 &&
    event.event_image.slice(1).length > 0;

  const eventTypeLabel = event?.event_type
    ? getEventTypeLabelById(event.event_type)
    : null;

  const organiserData = eventOrganiser as any;

  return (
    <>
      <div className={styles.eventDetail}>
        <div className={styles.main}>
          <section className={styles.sidebar}>
            <div className={styles.photo}>
              {hasImage ? (
                <img
                  src={event?.event_image?.[0]?.public_link}
                  alt="event-image"
                />
              ) : (
                <CardMedia
                  className={styles.placeholderImage}
                  image={""}
                  component={"div"}
                  title="event-image"
                />
              )}
              <div className={styles.eventType}>{eventTypeLabel}</div>
            </div>
            <div className={styles.orgWrapper}>
              <h4>{organiserData.name || organiserData.organisation_name}</h4>
              <p>{organiserData.organisation_info}</p>
              <div className={styles.orgInfo}>
                <img
                  src={organiserData?.logo}
                  alt="organiser-logo"
                  className={styles.orgLogo}
                />
              </div>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  navigate(`/organisation/${event?.organiser?.organiser_id}`)
                }
              >
                {t("eventDetail.showOrgProfile")}
              </Button>
            </div>
            {hasAdditionalImages && (
              <div className={styles.gallery}>
                <GalleryIcon />
                <div className={styles.images}>
                  {event?.event_image
                    ?.slice(1)
                    ?.map((img: any, idx: number) => {
                      return (
                        <img
                          key={img.id || idx}
                          src={img.public_link}
                          alt="event-image-minimized"
                          width={84}
                          height={66}
                        />
                      );
                    })}
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForwardOutlinedIcon />}
                    onClick={() => setGalleryOpen(true)}
                    className={styles.showAll}
                  >
                    {t("eventDetail.showAll")}
                  </Button>
                  <Lightbox
                    open={galleryOpen}
                    close={() => setGalleryOpen(false)}
                    slides={event?.event_image?.slice(1).map((img: any) => ({
                      src: img.public_link,
                      alt: "event-image",
                    }))}
                    plugins={[Thumbnails]}
                  />
                </div>
              </div>
            )}
            {organiserData.website_link && (
              <a
                href={`https://${organiserData.website_link}`}
                target="_blank"
                className={styles.website}
                rel="noreferrer"
              >
                <GlobeIcon />
                {organiserData.website_link}
              </a>
            )}
          </section>
          <section className={styles.content}>
            <div className={styles.titleWrapper}>
              <h2 className={styles.title}>{event.event_title}</h2>
              <div className={styles.pills}>
                {isUserAttending ? (
                  <Chip
                    color="success"
                    variant="filled"
                    size="small"
                    label={t("eventDetail.expectedCount")}
                    className={styles.pillAttend}
                  />
                ) : (
                  event.interests?.map((item: number, idx: number) => (
                    <Chip
                      key={idx}
                      color="primary"
                      variant="filled"
                      size="small"
                      label={getInterestLabelById(item)}
                      className={styles.pill}
                    />
                  ))
                )}
              </div>
            </div>
            <p className={styles.text}>
              <PinDropOutlinedIcon fontSize="small" />
              {event.address}
            </p>
            <p className={styles.text}>
              <CalendarMonthOutlinedIcon fontSize="small" />
              {event.event_start_datetime &&
                format(
                  new Date(event.event_start_datetime),
                  t("formats.dateLong"),
                  { locale: dateLocale() },
                )}
              <span>{t("eventCard.from")}&nbsp; </span>
              {event.event_start_datetime &&
                format(new Date(event.event_start_datetime), t("formats.time"), {
                  locale: dateLocale(),
                })}
            </p>
            {isUserAttending && (
              <div className={styles.calendarAdd}>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={handleDownloadCalendarEvent}
                >
                  {t("eventDetail.addToCalendar")}
                </Button>
              </div>
            )}
            <div className={styles.attendees}>
              {t("eventDetail.attendees")} <span>({attendeesCount})</span>
              {typeof event.capacity === "number" && (
                <>
                  {" "}
                  {t("eventDetail.capacity")} <span>({event.capacity})</span>
                </>
              )}
              <div className={styles.avatars}>
                {!attendeesCount ? (
                  <Avatar sx={{ width: 34, height: 34 }}>0</Avatar>
                ) : (
                  <>
                    {previewMatches
                      ?.slice(0, 5)
                      .map((match: any, idx: number) => {
                        const initials = getInitials(match?.first_name, "");
                        return (
                          <Avatar
                            key={idx}
                            sx={{ width: 34, height: 34 }}
                            src={match?.profile_image}
                          >
                            {initials}
                          </Avatar>
                        );
                      })}
                    {attendeesCount > 5 && (
                      <Avatar sx={{ width: 34, height: 34 }}>
                        {`+${attendeesCount - 5}`}
                      </Avatar>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className={styles.info}>
              <div>
                <h4>{t("eventDetail.detailsHeading")}</h4>
                <p className={styles.text}>{event.details}</p>
              </div>
              <div>
                <h4>{t("eventDetail.skillsHeading")}</h4>
                <ul className={styles.list}>
                  {event.skills?.map((skill: number, idx: number) => (
                    <li key={idx} className={styles.item}>
                      {getSkillLabelById(skill)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </div>
        <div className={styles.footer}>
          <Button
            color="primary"
            variant="outlined"
            onClick={() =>
              isUserAttending ? setDialogOpen("cancel") : handleNext()
            }
          >
            {isUserAttending
              ? t("eventDetail.cannotAttend")
              : t("eventDetail.lookingForOther")}
          </Button>
          {isUserAttending && !isEventFull && (
            <Button
              color="primary"
              variant="contained"
              onClick={() => setInviteDialogOpen(true)}
            >
              {t("eventCard.share")}
            </Button>
          )}
          {!isUserAttending && (
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleAttendClick()}
            >
              {t("eventDetail.join")}
            </Button>
          )}
        </div>
      </div>
      {/* @ts-ignore - JS component */}
      <InviteFriendsDialog
        event={event as any}
        dialogOpen={inviteDialogOpen}
        setDialogOpen={setInviteDialogOpen}
      />
      <CompleteProfileDialog
        open={completeProfileDialogOpen}
        onClose={() => setCompleteProfileDialogOpen(false)}
        onComplete={() => {
          // After completing profile, show the confirm dialog
          setDialogOpen("confirm");
        }}
      />
      <Dialog
        open={dialogOpen !== ""}
        disableEscapeKeyDown
        onClose={() => {}}
        slotProps={{
          backdrop: {
            onClick: () => {},
          },
        }}
        aria-labelledby="event-dialog-title"
        aria-describedby="event-dialog-description"
        maxWidth="xs"
        className={styles.dialog}
      >
        <DialogTitle id="event-dialog-title" className={styles.dialogTitle}>
          {dialogOpen === "confirm" ? (
            <>
              <LightningIcon />
              {t("eventDetail.confirmJoinTitle")}
            </>
          ) : (
            <>
              <WarningIcon />
              {t("eventDetail.confirmCancelTitle")}
            </>
          )}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <DialogContentText id="event-dialog-description">
            {dialogOpen === "confirm"
              ? t("eventDetail.confirmJoinText")
              : t("eventDetail.confirmCancelText")}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            fullWidth
            color={dialogOpen === "confirm" ? "primary" : "inherit"}
            onClick={() => setDialogOpen("")}
          >
            {t("eventDetail.decline")}
          </Button>
          <Button
            variant="contained"
            fullWidth
            color={dialogOpen === "confirm" ? "primary" : "error"}
            onClick={() =>
              dialogOpen === "confirm"
                ? handleAttendConfirm()
                : handleAttendCancel(matchId)
            }
          >
            {dialogOpen === "confirm"
              ? t("eventDetail.confirmJoinYes")
              : t("eventDetail.confirmCancelYes")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
